// src/hooks/useSubjects.js
// Loads all tax_subjects owned by the current user.
// Exposes createSubject, updateSubject, deleteSubject.
// Manages active subject selection (persisted to sessionStorage).
// Manages active tax year selection (persisted to sessionStorage).

import { useState, useEffect, useCallback } from 'react'
import { useAuth } from '../context/AuthContext'
import { supabase } from '../lib/supabase'

const SESSION_KEY_SUBJECT = 'active_subject_id'
const SESSION_KEY_YEAR    = 'active_tax_year'

// ── Tax year default logic ─────────────────────────────────────────────────
// move_date: JS Date object or null
// Returns the integer year the user most likely needs help with.
function deriveDefaultTaxYear(moveDate) {
  const now       = new Date()
  const thisYear  = now.getFullYear()
  const beforeJuly = now.getMonth() < 6  // month is 0-indexed; <6 = before July 1

  if (!moveDate) {
    // No move date known — fall back to standard filing-season logic
    return beforeJuly ? thisYear - 1 : thisYear
  }

  const moveYear = moveDate.getFullYear()

  if (moveYear === thisYear) {
    // Moving this year — their first Spanish tax year, most pressing
    return thisYear
  }

  if (beforeJuly) {
    // Moved a prior year, still in filing season — prior year is most pressing
    return thisYear - 1
  }

  // Moved a prior year, past July 1 — current year is now the focus
  return thisYear
}

export function useSubjects() {
  const { user } = useAuth()
  const [subjects, setSubjects]             = useState([])
  const [loading, setLoading]               = useState(true)
  const [error, setError]                   = useState(null)
  const [activeSubjectId, setActiveSubjectIdState] = useState(
    () => sessionStorage.getItem(SESSION_KEY_SUBJECT) || null
  )
  const [activeTaxYear, setActiveTaxYearState] = useState(
    () => {
      const stored = sessionStorage.getItem(SESSION_KEY_YEAR)
      return stored ? parseInt(stored, 10) : null  // null until profile loaded
    }
  )

  // ── Load subjects ──────────────────────────────────────────────────────────
  const loadSubjects = useCallback(async () => {
    if (!user) return
    setLoading(true)
    setError(null)
    try {
      const { data, error: fetchError } = await supabase
        .from('tax_subjects')
        .select('*')
        .eq('owner_user_id', user.id)
        .order('created_at', { ascending: true })

      if (fetchError) throw fetchError

      setSubjects(data || [])

      // Resolve active subject
      const currentActive = sessionStorage.getItem(SESSION_KEY_SUBJECT)
      const stillValid    = data?.some(s => s.id === currentActive)
      if (!stillValid) {
        const selfSubject = data?.find(s => s.subject_type === 'self')
        const defaultId   = selfSubject?.id || data?.[0]?.id || null
        setActiveSubjectIdState(defaultId)
        if (defaultId) sessionStorage.setItem(SESSION_KEY_SUBJECT, defaultId)
        else sessionStorage.removeItem(SESSION_KEY_SUBJECT)
      }

      // Always resolve tax year from DB — never trust stale sessionStorage
      const activeId = stillValid
        ? currentActive
        : data?.find(s => s.subject_type === 'self')?.id || data?.[0]?.id || null

      if (activeId) {
        // Prefer the most recent tax_year_profile year over derivation
        const { data: yearProfiles } = await supabase
          .from('tax_year_profiles')
          .select('tax_year')
          .eq('subject_id', activeId)
          .order('tax_year', { ascending: false })
          .limit(1)

        if (yearProfiles?.length > 0) {
          const resolvedYear = yearProfiles[0].tax_year
          setActiveTaxYearState(resolvedYear)
          sessionStorage.setItem(SESSION_KEY_YEAR, String(resolvedYear))
        } else {
          // Fall back to derivation if no year profiles exist yet
          const { data: profile } = await supabase
            .from('tax_profiles')
            .select('move_date')
            .eq('subject_id', activeId)
            .maybeSingle()
          const moveDate = profile?.move_date ? new Date(profile.move_date) : null
          const defaultYear = deriveDefaultTaxYear(moveDate)
          setActiveTaxYearState(defaultYear)
          sessionStorage.setItem(SESSION_KEY_YEAR, String(defaultYear))
        }
      } else {
        const fallback = deriveDefaultTaxYear(null)
        setActiveTaxYearState(fallback)
        sessionStorage.setItem(SESSION_KEY_YEAR, String(fallback))
      }
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }, [user])

  useEffect(() => {
    loadSubjects()
  }, [loadSubjects])

  // ── Active subject (derived) ───────────────────────────────────────────────
  const activeSubject = subjects.find(s => s.id === activeSubjectId)
    || subjects.find(s => s.subject_type === 'self')
    || null

  function setActiveSubject(subjectOrId) {
    const id = typeof subjectOrId === 'string' ? subjectOrId : subjectOrId?.id
    setActiveSubjectIdState(id)
    if (id) sessionStorage.setItem(SESSION_KEY_SUBJECT, id)
    else sessionStorage.removeItem(SESSION_KEY_SUBJECT)

    // When switching subjects, re-derive tax year from the new subject's profile
    // Clear stored year so loadSubjects re-derives on next reload,
    // or derive immediately if we have the profile data available
    sessionStorage.removeItem(SESSION_KEY_YEAR)
    setActiveTaxYearState(null)

    if (id) {
      // Prefer most recent tax_year_profile year, fall back to derivation
      supabase
        .from('tax_year_profiles')
        .select('tax_year')
        .eq('subject_id', id)
        .order('tax_year', { ascending: false })
        .limit(1)
        .then(({ data: yearProfiles }) => {
          if (yearProfiles?.length > 0) {
            const year = yearProfiles[0].tax_year
            setActiveTaxYearState(year)
            sessionStorage.setItem(SESSION_KEY_YEAR, String(year))
          } else {
            supabase
              .from('tax_profiles')
              .select('move_date')
              .eq('subject_id', id)
              .maybeSingle()
              .then(({ data: profile }) => {
                const moveDate    = profile?.move_date ? new Date(profile.move_date) : null
                const defaultYear = deriveDefaultTaxYear(moveDate)
                setActiveTaxYearState(defaultYear)
                sessionStorage.setItem(SESSION_KEY_YEAR, String(defaultYear))
              })
          }
        })
    }
  }

  function setActiveTaxYear(year) {
    const y = typeof year === 'number' ? year : parseInt(year, 10)
    setActiveTaxYearState(y)
    sessionStorage.setItem(SESSION_KEY_YEAR, String(y))
  }

  // ── CRUD ───────────────────────────────────────────────────────────────────
  // NOTE (ticket 052, migration 017): the browser can no longer set consent_status,
  // partner_id, lead_status or case_brief on tax_subjects (column privileges). This
  // function is dormant (its only caller, SubjectsPage, has no live route) and the
  // consent_status it sends is rejected by the column privileges from migration 017.
  async function createSubject({ display_name, subject_type = 'client', relationship_to_owner = 'client', source = 'app', consent_status = 'advisor_entered', notes = null }) {
    if (!user) return { error: 'Not authenticated' }
    const { data, error } = await supabase
      .from('tax_subjects')
      .insert({
        owner_user_id: user.id,
        display_name,
        subject_type,
        relationship_to_owner,
        source,
        consent_status,
        notes,
      })
      .select()
      .single()
    if (!error) setSubjects(prev => [...prev, data])
    return { data, error }
  }

  async function updateSubject(id, updates) {
    const { data, error } = await supabase
      .from('tax_subjects')
      .update(updates)
      .eq('id', id)
      .eq('owner_user_id', user.id)
      .select()
      .single()
    if (!error) setSubjects(prev => prev.map(s => s.id === id ? data : s))
    return { data, error }
  }

  async function deleteSubject(id) {
    const subject = subjects.find(s => s.id === id)
    if (subject?.subject_type === 'self') return { error: 'Cannot delete your own profile' }

    const { error } = await supabase
      .from('tax_subjects')
      .delete()
      .eq('id', id)
      .eq('owner_user_id', user.id)

    if (!error) {
      setSubjects(prev => prev.filter(s => s.id !== id))
      if (activeSubjectId === id) {
        const selfSubject = subjects.find(s => s.subject_type === 'self' && s.id !== id)
        setActiveSubject(selfSubject?.id || null)
      }
    }
    return { error }
  }

  // ── ensureSelfSubject ──────────────────────────────────────────────────────
  // The single, race-safe way to "make sure this user has a self subject."
  // Every call site that needs this (AppShell, ProfilePage, AuthCallback,
  // claim-session) should go through here instead of reimplementing its own
  // check-then-insert — that's what produced duplicate 'self' subjects
  // before (two near-simultaneous callers both passing the check before
  // either insert committed).
  //
  // Relies on the tax_subjects_one_self_per_owner partial unique index
  // (live in the database; not defined in a repo migration) to close the race at the
  // database layer: if two calls race, the loser's insert fails with a
  // 23505 (unique_violation) instead of silently creating a duplicate —
  // we catch that and re-fetch the row the winner created.
  const ensureSelfSubject = useCallback(async (displayName) => {
    if (!user) return { error: 'Not authenticated' }

    // Fast path: we already have it loaded.
    const existing = subjects.find(s => s.subject_type === 'self')
    if (existing) return { data: existing, error: null }

    // Slow path: check the DB directly in case local state is stale
    // (e.g. this is the very first load and loadSubjects hasn't run yet).
    const { data: dbExisting, error: lookupError } = await supabase
      .from('tax_subjects')
      .select('*')
      .eq('owner_user_id', user.id)
      .eq('subject_type', 'self')
      .maybeSingle()

    if (lookupError) return { error: lookupError.message }
    if (dbExisting) {
      setSubjects(prev => prev.some(s => s.id === dbExisting.id) ? prev : [...prev, dbExisting])
      return { data: dbExisting, error: null }
    }

    // Attempt to create. If a concurrent caller wins the race, the unique
    // index rejects this insert — catch that specifically and re-fetch
    // rather than surfacing an error to the user.
    const { data: created, error: insertError } = await supabase
      .from('tax_subjects')
      .insert({
        owner_user_id:  user.id,
        display_name:   displayName || user.email?.split('@')[0] || 'My Profile',
        subject_type:   'self',
        source:         'app',
      })
      .select()
      .single()

    if (!insertError) {
      setSubjects(prev => [...prev, created])
      return { data: created, error: null }
    }

    if (insertError.code === '23505') {
      const { data: winner, error: refetchError } = await supabase
        .from('tax_subjects')
        .select('*')
        .eq('owner_user_id', user.id)
        .eq('subject_type', 'self')
        .single()
      if (refetchError) return { error: refetchError.message }
      setSubjects(prev => prev.some(s => s.id === winner.id) ? prev : [...prev, winner])
      return { data: winner, error: null }
    }

    return { error: insertError.message }
  }, [user, subjects])

  return {
    subjects,
    loading,
    error,
    activeSubject,
    activeSubjectId,
    setActiveSubject,
    activeTaxYear,
    setActiveTaxYear,
    createSubject,
    ensureSelfSubject,
    updateSubject,
    deleteSubject,
    reload: loadSubjects,
  }
}